import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ServiceCard from "@/components/ServiceCard";
import { Funnel, MagnifyingGlass, X } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const SORTS = [
  ["best_match", "Best match"],
  ["rating", "Highest rated"],
  ["newest", "Newest"],
  ["price_asc", "Price: Low to High"],
  ["price_desc", "Price: High to Low"],
  ["most_orders", "Most orders"],
];

function FiltersPanel({ filters, update, categories, onClose }) {
  return (
    <div className="space-y-6">
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          data-testid="browse-search-input"
          placeholder="Search..."
          value={filters.q}
          onChange={(e) => update("q", e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-mv-primary focus:ring-2 focus:ring-mv-primary/20"
        />
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Category</div>
        <div className="space-y-1 max-h-72 overflow-auto pr-1">
          <button
            onClick={() => update("category", "")}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm ${!filters.category ? "bg-mv-primary text-white font-bold" : "hover:bg-gray-100"}`}
            data-testid="filter-cat-all"
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => update("category", c.slug)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${filters.category === c.slug ? "bg-mv-primary text-white font-bold" : "hover:bg-gray-100"}`}
              data-testid={`filter-cat-${c.slug}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Seller tier</div>
        <Select value={filters.tier || "any"} onValueChange={(v) => update("tier", v === "any" ? "" : v)}>
          <SelectTrigger data-testid="filter-tier"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any tier</SelectItem>
            <SelectItem value="new">New seller</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="certified">Certified Expert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">SEO risk</div>
        <Select value={filters.seo_risk || "any"} onValueChange={(v) => update("seo_risk", v === "any" ? "" : v)}>
          <SelectTrigger data-testid="filter-risk"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="low">Low risk</SelectItem>
            <SelectItem value="medium">Medium risk</SelectItem>
            <SelectItem value="high">High risk</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Max price</div>
        <input
          type="number" placeholder="e.g. 500" value={filters.max_price}
          onChange={(e) => update("max_price", e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
          data-testid="filter-max-price"
        />
      </div>

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => {
          update("__reset__", true);
        }} data-testid="filter-reset">
          Reset filters
        </Button>
        {onClose && (
          <Button className="flex-1 bg-mv-primary text-white" onClick={onClose} data-testid="filter-apply">
            Show results
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    category: params.get("category") || "",
    sort: params.get("sort") || "best_match",
    tier: params.get("tier") || "",
    seo_risk: params.get("seo_risk") || "",
    max_price: params.get("max_price") || "",
    min_rating: params.get("min_rating") || "",
  });

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
  }, []);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.q) p.set("q", filters.q);
    if (filters.category) p.set("category", filters.category);
    if (filters.sort) p.set("sort", filters.sort);
    if (filters.tier) p.set("tier", filters.tier);
    if (filters.seo_risk) p.set("seo_risk", filters.seo_risk);
    if (filters.max_price) p.set("max_price", filters.max_price);
    if (filters.min_rating) p.set("min_rating", filters.min_rating);
    p.set("limit", "24");
    return p.toString();
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    api.get(`/services?${queryString}`).then(({ data }) => {
      setItems(data.items);
      setLoading(false);
    });
    setParams(new URLSearchParams(queryString));
  }, [queryString, setParams]);

  const update = (k, v) => {
    if (k === "__reset__") {
      setFilters({ q:"", category:"", sort:"best_match", tier:"", seo_risk:"", max_price:"", min_rating:"" });
      return;
    }
    setFilters((f) => ({ ...f, [k]: v }));
  };

  const activeCount = [filters.q, filters.category, filters.tier, filters.seo_risk, filters.max_price].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
      <div className="mb-6 md:mb-8">
        <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl text-mv-secondary">Browse services</h1>
        <p className="text-gray-600 mt-2 text-sm sm:text-base">Vetted digital service providers across {categories.length} categories.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block lg:col-span-3" data-testid="browse-filters-desktop">
          <FiltersPanel filters={filters} update={update} categories={categories} />
        </aside>

        {/* Results */}
        <div className="lg:col-span-9">
          <div className="flex items-center justify-between gap-2 mb-5 sm:mb-6 flex-wrap">
            <div className="text-xs sm:text-sm text-gray-600">
              {loading ? "Loading…" : `${items.length} service${items.length !== 1 ? "s" : ""}`}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {/* Mobile/tablet filter trigger */}
              <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden rounded-full" data-testid="mobile-filters-btn">
                    <Funnel size={16} weight="bold" className="mr-1.5" /> Filters
                    {activeCount > 0 && (
                      <span className="ml-1.5 bg-mv-primary text-white rounded-full text-[10px] px-1.5 py-0.5 font-bold">{activeCount}</span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[90vw] max-w-md overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-4">
                    <FiltersPanel filters={filters} update={update} categories={categories} onClose={() => setSheetOpen(false)} />
                  </div>
                </SheetContent>
              </Sheet>
              <Select value={filters.sort} onValueChange={(v) => update("sort", v)}>
                <SelectTrigger className="w-44 sm:w-56" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORTS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {items.length === 0 && !loading ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-12 text-center">
              <p className="text-gray-500">No services match your filters. Try adjusting them.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
              {items.map((s) => <ServiceCard key={s.id} service={s} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
